import { EventEmitter, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import SmoothScrollbar from 'smooth-scrollbar';
import { ScrollStatus, ScrollIntoViewOptions } from 'smooth-scrollbar/interfaces';
import { LANG } from '../models/enums/lang.enum';
import { LocalStorageEnum } from '../models/enums/local-storage.enum';
import { HeaderTitle } from '../models/header-title';
import { LocalStorageService } from './local-storage.service';
import { RequestService } from './request.service';
import { Observable, lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ConfirmDialogButtonComponent } from '../components/shares/confirm-dialog-button/confirm-dialog-button.component';
import { CONFIRM_TYPE } from '../components/shares/confirm-dialog/confirm-dialog.component';
import { LocalizeSnackbarComponent } from '../components/shares/snackbars/localize-snackbar/localize-snackbar.component';
import { User } from '../models/user';
import { FileViewerDialogComponent } from '../components/shares/file-viewer-dialog/file-viewer-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  allowed_name_sign_codes: Array<number> = [
    44, // ,
    45, // -
    47, // /
    32, // space (in both khmer and english)
  ];

  allowed_sign_codes: Array<number> = [
    46, // .
    44, // ,
    40, // (
    41, // )
    64, // @
    38, // &
    45, // -
    95, // _
    58, // :
    59, // ;
    39, // '
    34, // "
    32, // space (in both khmer and english)
  ];

  roman_number_chars: Array<number> = [
    73, // I
    86, // V
    88, // X
    76, // L
    67, // C
  ];

  currentLang!: LANG;
  menuChange = new EventEmitter<boolean>();
  navigationScroll = new EventEmitter<ScrollStatus>();
  headerTitle = new EventEmitter<HeaderTitle | null>();
  showActionButton = new EventEmitter<boolean>();
  navigationScrollRef!: SmoothScrollbar;

  constructor(
    private translate: TranslateService,
    private localStorage: LocalStorageService,
    private request: RequestService,
    private router: Router,
    private snackbar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  maxTodayDate() {
    return moment().toDate();
  }

  maxDob(year_config: number = 15) {
    return moment().subtract(year_config, 'years').toDate();
  }

  /**
  * @description: Show (value: string) on toolbar
  * @=====================================================
  *
  * @usage: this._utility.setHeaderTitle({ titleKM: nameKh, titleEN: nameEn, code: data.id});
  * @param title
  */
  setHeaderTitle(title: HeaderTitle | null = null) {
    this.headerTitle.emit(title);
  }


  /**
   * @description: Scroll into view
   * @=====================================================
   *
   * @usage: this._utility.scrollIntoView('some_html_element_id')
   * @param el
   * @param options
   */
  scrollIntoView(el: HTMLElement, options?: Partial<ScrollIntoViewOptions>): void {
    this.navigationScrollRef.scrollIntoView(el, options)
  }


  /**
   * @description: Reset scroll back to start position
   * (top: 0, left: 0)
   * @=====================================================
   *
   * @usage: this._utility.resetNavigationScroll();
   */
  resetNavigationScroll(): void {
    this.navigationScrollRef.scrollTo(0, 0, 100);
  }


  /**
   * @description: Initialize app translation
   * @=====================================================
   *
   * @usage: this._utility.initializeTranslate();
   */
  initializeTranslate(): number {
    this.currentLang = this.localStorage.get(LocalStorageEnum.lang) as LANG;
    const lang = this.currentLang || LANG.KM;
    return this.setDefaultLang(lang);
  }


  /**
   * @description: Set app default lang ('km' | 'en')
   * @=====================================================
   *
   * @usage: this.setDefaultLang(lang);
   * @param lang
   */
  setDefaultLang(lang: LANG): number {
    if (!this.availableLangs.includes(lang)) lang = LANG.KM;
    try {
      this.localStorage.set(LocalStorageEnum.lang, lang);
      this.translate.use(lang);
      this.currentLang = lang;
      return 1;
    } catch (error) {
      return 0;
    }
  }


  /**
   * @description: List supporting langugages
   * @=====================================================
   */
  get availableLangs(): LANG[] {
    return [LANG.KM, LANG.EN];
  }


  /**
   * @description: Get saved lang
   * on localstorage ('km' | 'en')
   * @=====================================================
   */
  getLang() {
    let lang = this.localStorage.get(LocalStorageEnum.lang);
    if (!lang) lang = 'km';

    return lang;
  }


  /**
   * @description: Description not available
   * @=====================================================
   */
  getLocaleIdValue() {
    return this.translate.currentLang == LANG.KM ? 'km_KH' : 'en_US';
  }


  /**
   * @description: Get branch
   * @=====================================================
   */
  getBranch(): Observable<any> {
    return this.request.postJSON('/branch', {
      option: {
        is_loading: true
      }
    })
  }


  /**
   * @description: Get Branches
   * @=====================================================
   */
  getBranches(query: any): Observable<any> {
    return this.request.getJSON('/branch', {
      data: query,
      option: {
        is_loading: true
      }
    })
  }


  /**
   * @description: Get Manage Branches
   * @=====================================================
   */
  getManageBranches(query: any): Observable<any> {
    return this.request.getJSON('/manage-branch', {
      data: query,
      option: {
        is_loading: true
      }
    })
  }


  /**
   * @description: Get permissions of
   * current login user
   * @=====================================================
   *
   * @usage: let permissions = this._utility.getArrayPermission();
   */
  getArrayPermission() {
    return this.localStorage.getArray(LocalStorageEnum.permissions);
  }


  /**
   * @description: Get Current Login User Information
   * @=====================================================
   *
   * @usage: let curentUser = this._utility.getCurrentLoginUser();
   */
  getCurrentLoginUser() {
    let value: any = this.localStorage.decryptSpecialCharacter(LocalStorageEnum.user);
    return JSON.parse(value ? value : '') as User;
  }


  /**
   * @description: Get expired time of
   * current loged in token
   * @=====================================================
   *
   * @usage: let exp_time = this._utility.getExpiredTime();
   */
  getExpiredTime() {
    return parseInt(this.localStorage.get(LocalStorageEnum.expiry_time)) * 1000;
  }


  /**
   * @description: When login success,
   * save data on localstorage
   * @=====================================================
   */
  onSetUpSuccessLogin(res: any) {
    let expiry_time = new Date(res.data.expiresIn).getTime();
    this.localStorage.set(LocalStorageEnum.lang, this.getLang());
    this.localStorage.set(LocalStorageEnum.token, res.data.token);
    this.localStorage.set(LocalStorageEnum.expiry_time, expiry_time.toString());
    this.localStorage.set(LocalStorageEnum.refresh_token, res.data.refresh_token);
    this.localStorage.encryptSpecialCharacter(LocalStorageEnum.user, JSON.stringify(res.data.user));
  }


  /**
   * @description: When get new token,
   * save new token information on localstorage
   * @=====================================================
   */
  onSetUpRefreshToken(res: any) {
    let expiry_time = res.data.expiresIn;
    this.localStorage.set(LocalStorageEnum.token, res.data.token);
    this.localStorage.set(LocalStorageEnum.refresh_token, res.data.refresh_token);
    this.localStorage.set(LocalStorageEnum.expiry_time, expiry_time.toString());
  }


  /**
   * @description: When log out of app,
   * clean localstorage and navigate to login
   * @=====================================================
   */
  onSignOut() {
    this.localStorage.delete(LocalStorageEnum.token);
    this.localStorage.delete(LocalStorageEnum.expiry_time);
    this.localStorage.delete(LocalStorageEnum.refresh_token);
    this.localStorage.delete(LocalStorageEnum.permissions);
    this.localStorage.delete(LocalStorageEnum.user);
    this.router.navigate(['/login']);
  }


  /**
   * @description: Clean authentication information
   * @=====================================================
   */
  onCleanAuthInfo() {
    this.localStorage.delete(LocalStorageEnum.token);
    this.localStorage.delete(LocalStorageEnum.expiry_time);
    this.localStorage.delete(LocalStorageEnum.refresh_token);
    this.localStorage.delete(LocalStorageEnum.permissions);
    this.localStorage.delete(LocalStorageEnum.user);
  }


  /**
   * @description: Open error snackbar with translation
   * @note: make sure 'msg' is in valid translate format
   * @=====================================================
   *
   * @usage: this._utility.openErrorSnackbar('example.msg')
   * @param msg
   * @param [duration=1500]
   */
  openErrorSnackbar(msg: string, params?: object, duration: number = 1500) {
    this.snackbar.openFromComponent(LocalizeSnackbarComponent, {
      duration: duration,
      panelClass: 'panel-error',
      data: {
        msg: msg,
        params: params
      }
    })
  }


  /**
   * @description: Open success snackbar with translation
   * @note: make sure 'msg' is in valid translate format
   * @=====================================================
   *
   * @usage: this._utility.openSuccessSnackbar('example.msg')
   * @param msg
   * @param [duration=1500]
   */
  openSuccessSnackbar(msg: string, params?: object, duration: number = 1500) {
    this.snackbar.openFromComponent(LocalizeSnackbarComponent, {
      duration: duration,
      panelClass: 'panel-success',
      data: {
        msg: msg,
        params: params
      }
    })
  }


  /**
   * @description: Open simple confirm dialog
   * @note:
   * - title: must be in translate format. E.g. ('test.msg')
   * - confirm_desc: must be in translate format. E.g. ('test.desc')
   * - localize structure: 'dialog.confirm.'
   * @=====================================================
   *
   * @usage: this._utility.openConfirmDialog({title: 'test.msg', confirm_desc: 'test.desc'})
   * @param data
   */
  async openConfirmDialog(data: { title?: string, confirm_desc?: string, confirm_desc_param?: any, options?: any }) {
    const dialog = this.dialog.open(ConfirmDialogButtonComponent, {
      width: data?.options?.width || '500px',
      disableClose: true,
      autoFocus: false,
      data: {
        title: data?.title || ('dialog.confirm.alert'),
        confirm_desc: data?.confirm_desc || (''),
        confirm_desc_params: data?.confirm_desc_param,
        confirm_type: data?.options?.confirm_type ? data?.options?.confirm_type : CONFIRM_TYPE.NORMAL,
        ...data?.options
      }
    });

    const response = await lastValueFrom(dialog.afterClosed());
    return response;
  }


  /**
   * @description: Format date string to desired formats,
   * defualt format: ('YYYY-MM-DD')
   * @=====================================================
   *
   * @usage: this._utility.momentFormat('2023-10-13T00:00:00.000Z')
   * @param inp
   * @param [format = 'YYYY-MM-DD']
   */
  momentFormat(inp: string, format: string = "YYYY-MM-DD") {
    return moment(inp).locale("en-US").format(format);
  }

  validateKeypressKh(event:any, is_company_name: boolean = false) {
    var ew = event.which;
    var valid: boolean = false;
    if (ew == 32) valid = true;
    else if (is_company_name && this.allowed_sign_codes.includes(ew))
      valid = true;
    else if (is_company_name && 6112 <= ew && ew <= 6121) valid = true;
    // number in khmer (company name)
    else if (6112 <= ew && ew <= 6121) valid = false;
    // number in khmer (human name)
    else if (ew == 0x200b || (0 <= ew && ew <= 0x024f)) valid = false;
    else if (0x0590 <= ew && ew <= 0x05ff) valid = false;
    else valid = true;
    return valid;
  }

  validateKeypressEn(event:any, is_company_name: boolean = false) {
    var ew = event.which;
    var valid: boolean = false;
    if (ew == 32) valid = true;
    else if (!is_company_name && this.allowed_name_sign_codes.includes(ew))
      valid = true;
    else if (is_company_name && this.allowed_sign_codes.includes(ew))
      valid = true;
    else if (is_company_name && 48 <= ew && ew <= 57) valid = true;
    // number in english (company name)
    else if (48 <= ew && ew <= 57) valid = false;
    // number in english (human name)
    else if (65 <= ew && ew <= 90) valid = true;
    else if (97 <= ew && ew <= 122) valid = true;
    else valid = false;
    return valid;
  }
}
